import { useFetcher } from "~/hooks/useFetcher";
import {
  type PartialSkill,
  SKILL_LIST_LIMIT,
  type SkillFields,
  type SkillPage,
  attachDatasetSkillFromFields,
  attachDatasetSkillFromId,
  datasetSkillsListPath,
  detachDatasetSkill,
  invalidateAllSkillLists,
} from "~/services/skills";

const listKey = (datasetId: string) =>
  datasetSkillsListPath(datasetId, { limit: SKILL_LIST_LIMIT });

interface UseDatasetAttachedSkillsResult {
  skills: PartialSkill[];
  isLoading: boolean;
  error: unknown;
  attachFromLibrary: (skillId: string) => Promise<void>;
  createSkill: (fields: SkillFields) => Promise<void>;
  detach: (skillId: string) => Promise<void>;
}

export function useDatasetAttachedSkills(
  datasetId: string | undefined
): UseDatasetAttachedSkillsResult {
  const key = datasetId ? listKey(datasetId) : null;
  const { data, isLoading, error } = useFetcher<SkillPage>(key);
  const skills = data?.items ?? [];

  const attachFromLibrary = async (skillId: string) => {
    if (!datasetId) {
      return;
    }
    await attachDatasetSkillFromId(datasetId, skillId);
    await invalidateAllSkillLists();
  };

  const createSkill = async (fields: SkillFields) => {
    if (!datasetId) {
      return;
    }
    await attachDatasetSkillFromFields(datasetId, fields);
    await invalidateAllSkillLists();
  };

  const detach = async (skillId: string) => {
    if (!datasetId) {
      return;
    }
    await detachDatasetSkill(datasetId, skillId);
    await invalidateAllSkillLists();
  };

  return { skills, isLoading, error, attachFromLibrary, createSkill, detach };
}
